// Turn database/API errors into sentences a volunteer can act on.
//
// Errors we RAISE ourselves in SQL (code P0001: "Only central treasury can...",
// "Allocations exceed charge amount", ...) are already written in plain language,
// so they pass through. Everything else (constraint names, SQL fragments, RLS
// internals) is replaced: raw database text never reaches the screen.
import { FriendlyError } from './auth'

type DbError = { message?: string; code?: string; details?: string; status?: number }

export function friendlyDbError(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (err instanceof FriendlyError) return err.message
  const e = (err ?? {}) as DbError
  const msg = String(e.message ?? '')
  const code = String(e.code ?? '')

  if (/failed to fetch|network|load failed|timeout/i.test(msg) || e.status === 0) {
    return 'Could not reach the server. Check your internet connection and try again.'
  }
  if (/invalid api key|no api key/i.test(msg)) {
    return 'Invalid API key: this copy of the app is set up wrongly. Check VITE_SUPABASE_ANON_KEY in .env.'
  }
  if (e.status === 401 || code === 'PGRST301' || /jwt/i.test(msg)) {
    return 'Your session has ended. Please sign in again.'
  }
  // Our own rules, written for humans. Keep them short enough to display.
  if (code === 'P0001' && msg && msg.length <= 240) return msg
  if (code === '42501' || /row-level security|permission denied/i.test(msg)) {
    return 'You do not have permission to do that.'
  }
  // Two people changed the same records at the same moment: the database stopped one of them.
  if (code === '40P01' || code === '40001') {
    return 'Someone else was changing these records at the same moment. Look again and try once more.'
  }
  if (code === '23505') {
    if (/phone/i.test(msg + (e.details ?? ''))) return 'That phone number already belongs to another member.'
    return 'That already exists.'
  }
  if (code === '23503') return 'That refers to something that no longer exists. Refresh and try again.'
  if (code === '23514' || code === '22P02' || code === '22003') {
    return 'Some of what you entered is not valid. Check it and try again.'
  }
  return fallback
}

export class DataError extends Error {
  constructor(err: unknown, fallback?: string) {
    super(friendlyDbError(err, fallback))
  }
}

/** Throw a friendly error if a Supabase call failed, otherwise return its data. */
export function unwrap<T>(res: { data: T | null; error: unknown }, fallback?: string): T {
  if (res.error) throw new DataError(res.error, fallback)
  return res.data as T
}
